import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { PopupNames } from 'contexts/PopupContext';
import { MirrorService } from 'services/MirrorService';
import { AdsPlacement } from 'types/enums/AdsPlacement';

import { getIsSessionAdsItemShown, setSessionAdsItemShown } from 'helpers/ads';
import { isMonetizationPage, isTermsOrPolicyPage } from 'helpers/route';
import { useAdsQuery } from 'hooks/queries/useAdsQuery';
import { isNativeAdsEnabledSelector } from 'store/common/selectors';

import { useAdsPlatform } from './useAdsPlatform';
import { usePopup } from './usePopup';
import { useRefreshTimer } from './useRefreshTimer';

export const ADS_FLOATING_BUBBLE_DELAY = 1000 * 90; // 90 seconds
export const ADS_VIDEO_DELAY = 1000 * 60 * 5; // 5 minutes

export const ADS_FLOATING_BUBBLE = 'ADS_FLOATING_BUBBLE';
export const ADS_VIDEO = 'ADS_VIDEO';

export const useMultiAdsPopups = () => {
  const { pathname } = useLocation();

  const { getAdsPlatform } = useAdsPlatform();

  const isNativeAdsEnabled = useSelector(isNativeAdsEnabledSelector);

  const [isAdsFloatingFailed, setIsAdsFloatingFailed] = useState(false);
  const [isAdsVideoFailed, setIsAdsVideoFailed] = useState(false);

  const {
    handleOpenASAP: handleAdsFloatingBubbleOpenASAP,
    handleClose: handleAdsFloatingBubbleClose,
    checkIsOpenOrInQueue: checkIsFloatingBubbleOpenOrInQueue,
    handleAction: handleAdsFloatingBubbleAction,
  } = usePopup(PopupNames.AdsFloatingBubble);

  const {
    handleOpenASAP: handleAdsVideoOpenASAP,
    handleClose: handleAdsVideoClose,
    checkIsOpenOrInQueue: checkIseAdsVideoOpenOrInQueue,
    handleAction: handleAdsVideoAction,
  } = usePopup(PopupNames.AdsVideo);

  const { adsQuery: adsFloatingBubbleQuery } = useAdsQuery(
    {
      placement: AdsPlacement.FloatingBubble,
      platform: getAdsPlatform(),
    },
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          handleAdsFloatingBubbleOpenASAP(data);
          setSessionAdsItemShown(AdsPlacement.FloatingBubble);
        } else setIsAdsFloatingFailed(true);
      },
      onError: () => {
        setIsAdsFloatingFailed(true);
      },
    }
  );

  const { adsQuery: adsVideoPopupQuery } = useAdsQuery(
    {
      placement: AdsPlacement.AdVideoPopup,
      platform: getAdsPlatform(),
    },
    {
      enabled: false,
      onSuccess: (data) => {
        if (data) {
          handleAdsVideoOpenASAP(data);
          setSessionAdsItemShown(AdsPlacement.AdVideoPopup);
        } else setIsAdsVideoFailed(true);
      },
      onError: () => {
        setIsAdsFloatingFailed(true);
      },
    }
  );

  const {
    handleStartTimer: handleAdsFloatingBubbleStartTimer,
    handleStopTimer: handleAdsFloatingBubbleStopTimer,
    handleRemoveTimer: handleAdsFloatingBubbleRemoveTimer,
  } = useRefreshTimer({
    name: ADS_FLOATING_BUBBLE,
    ms: ADS_FLOATING_BUBBLE_DELAY,
    cb: () => adsFloatingBubbleQuery.refetch(),
  });

  const {
    handleStartTimer: handleAdsVideoStartTimer,
    handleStopTimer: handleAdsVideoStopTimer,
    handleRemoveTimer: handleAdsVideoRemoveTimer,
  } = useRefreshTimer({
    name: ADS_VIDEO,
    ms: ADS_VIDEO_DELAY,
    cb: () => adsVideoPopupQuery.refetch(),
  });

  useEffect(() => {
    handleAdsFloatingBubbleAction.current = (action: { type: string }) => {
      if (action.type === 'close') {
        handleAdsFloatingBubbleClose();
      }
    };

    handleAdsVideoAction.current = (action: { type: string }) => {
      if (action.type === 'close') {
        handleAdsVideoClose();
      }
    };
  }, [handleAdsVideoAction, handleAdsVideoClose]);

  useEffect(() => {
    const isAdsFloatingBubbleShown = getIsSessionAdsItemShown(
      AdsPlacement.FloatingBubble
    );

    if (
      !isAdsFloatingFailed &&
      !isAdsFloatingBubbleShown &&
      !checkIsFloatingBubbleOpenOrInQueue() &&
      !isMonetizationPage(pathname) &&
      !isTermsOrPolicyPage(pathname) &&
      isNativeAdsEnabled &&
      MirrorService.isSecondSiteGroup
    ) {
      handleAdsFloatingBubbleStartTimer();
    }

    return () => {
      handleAdsFloatingBubbleStopTimer();
    };
  }, [
    checkIsFloatingBubbleOpenOrInQueue,
    handleAdsFloatingBubbleStartTimer,
    handleAdsFloatingBubbleStopTimer,
    isAdsFloatingFailed,
    isNativeAdsEnabled,
    pathname,
  ]);

  useEffect(() => {
    const isAdsVideoPopupShown = getIsSessionAdsItemShown(
      AdsPlacement.AdVideoPopup
    );

    if (
      !isAdsVideoFailed &&
      !isAdsVideoPopupShown &&
      !checkIseAdsVideoOpenOrInQueue() &&
      !isMonetizationPage(pathname) &&
      !isTermsOrPolicyPage(pathname) &&
      isNativeAdsEnabled &&
      MirrorService.isSecondSiteGroup
    ) {
      handleAdsVideoStartTimer();
    }

    return () => {
      handleAdsVideoStopTimer();
    };
  }, [
    checkIseAdsVideoOpenOrInQueue,
    handleAdsVideoStartTimer,
    handleAdsVideoStopTimer,
    isAdsVideoFailed,
    isNativeAdsEnabled,
    pathname,
  ]);

  useEffect(() => {
    if (isAdsFloatingFailed) {
      handleAdsFloatingBubbleRemoveTimer();
    }
  }, [handleAdsFloatingBubbleRemoveTimer, isAdsFloatingFailed]);

  useEffect(() => {
    if (isAdsVideoFailed) {
      handleAdsVideoRemoveTimer();
    }
  }, [handleAdsVideoRemoveTimer, isAdsVideoFailed]);

  useEffect(() => {
    return () => {
      setIsAdsFloatingFailed(false);
      setIsAdsVideoFailed(false);
    };
  }, [pathname]);
};
